import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box, Grid, Paper, Typography, Table, TableHead, TableRow, TableCell, TableBody,
  Chip, Button, Stack, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Alert, CircularProgress
} from '@mui/material';
import TroubleshootRoundedIcon from '@mui/icons-material/TroubleshootRounded';
import PageHeader from '../../components/common/PageHeader';
import ModuleControlPanel from '../../components/common/ModuleControlPanel';
import StatCard from '../../components/common/StatCard';
import { loadVulnerabilities, runScan } from './vulnSlice';
import { severityChipSx, severityLabel, formatRelativeTime } from '../../utils/severity';
import { tokens } from '../../theme/theme';

const JOB_STATUS_COLOR = {
  QUEUED: tokens.text.muted,
  RUNNING: tokens.severity.info,
  COMPLETED: tokens.severity.low,
  FAILED: tokens.severity.critical
};

export default function VulnerabilitiesPage() {
  const dispatch = useDispatch();
  const { items, summary, status, error, scanStatus, scanError } = useSelector((s) => s.vulnerabilities);
  const [scanDialogOpen, setScanDialogOpen] = useState(false);
  const [targetUrl, setTargetUrl] = useState('');

  useEffect(() => { dispatch(loadVulnerabilities()); }, [dispatch]);

  const handleRunScan = async () => {
    if (!targetUrl.trim() || scanStatus === 'loading') return;
    try {
      await dispatch(runScan(targetUrl.trim())).unwrap();
      setScanDialogOpen(false);
      setTargetUrl('');
      dispatch(loadVulnerabilities());
    } catch {
      // Keep the dialog open so the user can correct/retry the request.
    }
  };

  return (
    <Box>
      <PageHeader
        title="Vulnerability Assessment"
        subtitle="OWASP Top 10, CVEs, misconfigurations, and weak encryption across monitored assets"
        action={
          <Stack direction="row" spacing={1}>
            <Button variant="contained" startIcon={<TroubleshootRoundedIcon />} onClick={() => setScanDialogOpen(true)}>
              Run New Scan
            </Button>
            <ModuleControlPanel basePath="/vulnerabilities" moduleLabel="Vulnerability Assessment" />
          </Stack>
        }
      />

      {error && <Alert severity="warning" sx={{ mb: 2 }}>{error}</Alert>}
      {status === 'loading' && <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={28} /></Box>}

      {summary && (
        <Grid container spacing={2.5} sx={{ mb: 2.5 }}>
          <Grid item xs={6} sm={3}><StatCard label="Assets Scanned" value={summary.assetsScanned} /></Grid>
          <Grid item xs={6} sm={3}><StatCard label="Critical" value={summary.critical} accent={tokens.severity.critical} /></Grid>
          <Grid item xs={6} sm={3}><StatCard label="High" value={summary.high} accent={tokens.severity.high} /></Grid>
          <Grid item xs={6} sm={3}><StatCard label="Open Findings" value={summary.open} accent={tokens.severity.medium} /></Grid>
        </Grid>
      )}

      <Paper sx={{ overflow: 'hidden' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              {['Target', 'Status', 'Scan Types', 'Findings', 'Highest Severity', 'Started'].map((h) => (
                <TableCell key={h} sx={{ color: tokens.text.muted, fontSize: '0.68rem', textTransform: 'uppercase' }}>{h}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {(Array.isArray(items) ? items : []).map((v) => (
              <TableRow key={v.id} hover>
                <TableCell sx={{ fontFamily: '"JetBrains Mono", monospace', fontSize: '0.76rem', color: tokens.text.secondary }}>{v.target_host || v.target_url}</TableCell>
                <TableCell>
                  <Chip
                    label={v.status} size="small"
                    sx={{ color: JOB_STATUS_COLOR[v.status], border: `1px solid ${JOB_STATUS_COLOR[v.status]}`, backgroundColor: 'transparent' }}
                  />
                </TableCell>
                <TableCell sx={{ fontSize: '0.78rem', color: tokens.text.secondary }}>{(v.scan_types || []).join(', ') || '—'}</TableCell>
                <TableCell sx={{ fontSize: '0.8rem', color: tokens.text.primary }}>{v.finding_count}</TableCell>
                <TableCell>
                  {v.highest_severity
                    ? <Chip label={severityLabel(v.highest_severity)} size="small" sx={severityChipSx(v.highest_severity)} />
                    : <Typography sx={{ fontSize: '0.76rem', color: tokens.text.muted }}>—</Typography>}
                </TableCell>
                <TableCell sx={{ fontSize: '0.72rem', color: tokens.text.muted, whiteSpace: 'nowrap' }}>{formatRelativeTime(v.started_at)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>

      <Dialog open={scanDialogOpen} onClose={() => setScanDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Run New Vulnerability Scan</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus fullWidth margin="dense" label="Target URL"
            placeholder="https://example.com"
            value={targetUrl}
            onChange={(e) => setTargetUrl(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') handleRunScan(); }}
          />
        </DialogContent>
        {scanError && <Alert severity="error" sx={{ mx: 3, mt: 1 }}>{scanError}</Alert>}
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setScanDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleRunScan} disabled={!targetUrl.trim() || scanStatus === 'loading'}>{scanStatus === 'loading' ? 'Starting…' : 'Start Scan'}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
